import React, { useMemo } from 'react';

import {
  AiElementsScope,
  DOCK_ACTION,
  DOCK_PANEL_FULL_WIDTH_MEDIA_QUERY,
  Dialog,
  DialogContent,
  DialogTitle,
  Dock,
  DockPanel,
  DockPanelAction,
  DockPanelAnchor,
  DockPanelHeader,
  DockShelf,
  DockShelfButton,
  DockShelfCloseButton,
  DockShelfItem,
} from '@modernloop/ai-elements';
import { useTranslation } from '@modernloop/i18n';
import { useFlag } from '@modernloop/shared/feature-flag';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import useHistory from 'src/hooks/useHistory';

import AgentChat from './AgentChat';
import {
  AGENT_DOCK_PRESENTATION,
  AGENT_SURFACE,
  type AgentChatSession,
  type AgentDockTab,
  useAgentChatSession,
} from './AgentChatSessionProvider';
import EditableAgentThreadTitle from './EditableAgentThreadTitle';
import ThreadSwitcher from './ThreadSwitcher';
import { getAgentRouteForThread } from './agent/agentRoutes';
import { muiThemeToAiTokens } from './aiElementsTheme';
import { AGENT_DOCK_PRESENCE, useAgentDockPresence } from './useAgentDockPresence';

type DockDependencies = {
  enabled: boolean;
  history: { push: (route: string) => void };
  isNarrow: boolean;
  session: AgentChatSession;
  translate: (key: string, values?: Record<string, string | number>) => string;
  AgentChatComponent: React.ComponentType<React.ComponentProps<typeof AgentChat>>;
  ThreadSwitcherComponent: React.ComponentType<React.ComponentProps<typeof ThreadSwitcher>>;
  TitleComponent: React.ComponentType<React.ComponentProps<typeof EditableAgentThreadTitle>>;
};

const titleForDockTab = (tab: AgentDockTab, titleByThreadId: ReadonlyMap<string, string>, untitled: string): string =>
  titleByThreadId.get(tab.threadId) ?? untitled;

/**
 * Dashboard-lifetime hand-off rail and compact Agent panel.
 *
 * Docked conversations are direct re-entry affordances. The Agent tab owns complete history and
 * new-chat creation without duplicating a switcher inside the open compact panel.
 */
export const AgentChatDockView: React.FC<{ dependencies: DockDependencies; visible?: boolean }> = ({
  dependencies,
  visible = true,
}) => {
  const { enabled: globalAgentEnabled, history, isNarrow, session, translate: t } = dependencies;
  const { AgentChatComponent, ThreadSwitcherComponent, TitleComponent } = dependencies;
  const theme = useTheme();
  const tokens = useMemo(() => muiThemeToAiTokens(theme), [theme]);
  // The dialog wrapper and the panel's own full-viewport styling must flip at the same width, so
  // this asks the AI Elements breakpoint rather than MUI's `sm`, which is 40px narrower.
  const {
    threadId,
    threads,
    isStreaming,
    activeSurface,
    dockTabs,
    hasStartedConversation,
    newThread,
    openThread,
    selectThread,
    restoreDockThread,
    minimizeDockThread,
    closeDockThread,
  } = session;
  const untitled = t('hiring-dashboard-ai-assistant:agent.threads.untitled');
  const titleByThreadId = useMemo(
    () => new Map(threads.map((thread) => [thread.externalThreadId, thread.title ?? untitled])),
    [threads, untitled]
  );
  const activeDockTab = dockTabs.find(
    (tab) =>
      tab.threadId === threadId &&
      tab.presentation === AGENT_DOCK_PRESENTATION.RESTORED &&
      activeSurface === AGENT_SURFACE.DOCK
  );

  if (!globalAgentEnabled) {
    return null;
  }

  const statusForTab = (tab: AgentDockTab): string | null => {
    if (tab.threadId === threadId && isStreaming) {
      return t('hiring-dashboard-ai-assistant:agent.dock.streaming');
    }
    return tab.hasUnread ? t('hiring-dashboard-ai-assistant:agent.dock.unread') : null;
  };
  const labelForTab = (tab: AgentDockTab): string => {
    const title = titleForDockTab(tab, titleByThreadId, untitled);
    const status = statusForTab(tab);
    return status ? t('hiring-dashboard-ai-assistant:agent.dock.tabLabel', { status, title }) : title;
  };
  const handleExpand = () => {
    // Claim first so only the arriving full page may render the shared composer and live log.
    openThread(AGENT_SURFACE.FULL_PAGE);
    history.push(getAgentRouteForThread(threadId, hasStartedConversation));
  };
  // While the full page owns the transcript, a shelf action must switch its thread instead of
  // claiming the dock, which would blank the dedicated Agent page behind the small panel.
  const shelfSurface = activeSurface === AGENT_SURFACE.FULL_PAGE ? AGENT_SURFACE.FULL_PAGE : AGENT_SURFACE.DOCK;
  const openShelfThread = (nextThreadId: string) => {
    if (shelfSurface === AGENT_SURFACE.FULL_PAGE) {
      selectThread(nextThreadId, AGENT_SURFACE.FULL_PAGE);
      return;
    }
    restoreDockThread(nextThreadId);
  };
  const panelTitle = activeDockTab
    ? t('hiring-dashboard-ai-assistant:agent.dock.panelLabel', {
        title: titleForDockTab(activeDockTab, titleByThreadId, untitled),
      })
    : '';
  const activeConversationTitle = activeDockTab ? titleForDockTab(activeDockTab, titleByThreadId, untitled) : '';
  const panel = activeDockTab && (
    <DockPanel
      aria-label={panelTitle}
      className="sm:absolute sm:right-0 sm:bottom-0 sm:rounded-b-none sm:border-b-0"
      data-testid="agent-dock-panel"
    >
      {/* The header floats over the transcript as a translucent blur layer instead of taking flow
          space, so scrolled messages and the new-conversation gradient run underneath it. */}
      <DockPanelHeader className="absolute inset-x-0 top-0 z-10 bg-card/75 backdrop-blur-md dark:bg-card/70">
        {/* The shelf already switches open conversations, so the compact header identifies only
            the active conversation and keeps all window controls in one row. */}
        <TitleComponent
          key={activeDockTab.threadId}
          className="min-w-0 flex-1 truncate rounded-sm px-2 text-left text-sm font-medium hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          testId="agent-dock-title"
          threadId={activeDockTab.threadId}
          title={activeConversationTitle}
        />
        <DockPanelAction
          action={DOCK_ACTION.MINIMIZE}
          data-testid="agent-dock-minimize"
          label={t('hiring-dashboard-ai-assistant:agent.dock.minimize')}
          onClick={() => minimizeDockThread(activeDockTab.threadId)}
        />
        <DockPanelAction
          action={DOCK_ACTION.EXPAND}
          data-testid="agent-dock-expand"
          label={t('hiring-dashboard-ai-assistant:agent.dock.continueInAgentChat')}
          onClick={handleExpand}
        />
        <DockPanelAction
          action={DOCK_ACTION.CLOSE}
          data-testid="agent-dock-close"
          label={t('hiring-dashboard-ai-assistant:agent.dock.close')}
          onClick={() => closeDockThread(activeDockTab.threadId)}
        />
      </DockPanelHeader>
      <div className="min-h-0 flex-1">
        <AgentChatComponent focusOnMount={visible} surface={AGENT_SURFACE.DOCK} />
      </div>
    </DockPanel>
  );

  return (
    // A transform on an ancestor of a `position: fixed` element makes that ancestor the fixed
    // element's containing block, so the translated-away Dock would otherwise inflate the
    // document's scrollable overflow (ENG-26913 follow-up). The outer frame is pinned to the
    // viewport with no transform of its own, so it stays excluded from that overflow, and its
    // `overflow-hidden` clips the inner, transformed track that carries the slide animation.
    <div
      aria-hidden={!visible}
      className="pointer-events-none fixed inset-0 overflow-hidden"
      data-testid="agent-dock-motion"
      data-visible={visible}
      style={{ zIndex: theme.zIndex.modal }}
    >
      {/* The motion track moves each scoped portal, including an open narrow chat, with the shelf. */}
      <div
        className={`h-full transform-gpu transition-transform duration-300 ease-out motion-reduce:transition-none ${
          visible ? 'translate-y-0' : 'translate-y-[100dvh]'
        }`}
        data-testid="agent-dock-motion-track"
      >
        <AiElementsScope className="relative" tokens={tokens}>
          <Dock
            aria-label={t('hiring-dashboard-ai-assistant:agent.dock.label')}
            // Keep the desktop shelf out of the expanded 200 px application navigation.
            className="sm:left-[200px]"
            data-testid="agent-chat-dock"
          >
            {isNarrow ? (
              <Dialog
                onOpenChange={(open) => {
                  if (!open && activeDockTab) {
                    minimizeDockThread(activeDockTab.threadId);
                  }
                }}
                open={Boolean(activeDockTab) && visible}
              >
                <DialogContent
                  // The panel supplies its own window controls, so a second close affordance would
                  // duplicate them; the title stays for assistive technology only.
                  fullScreen
                  // Radix would focus the first window control and pop its tooltip. The panel itself
                  // is the correct landing point on open and keeps Escape bound to the dialog.
                  onOpenAutoFocus={(event) => {
                    event.preventDefault();
                    // SAFETY: Radix dispatches this event from the dialog content element.
                    (event.currentTarget as HTMLElement | null)?.focus();
                  }}
                  showCloseButton={false}
                >
                  <DialogTitle className="sr-only">{panelTitle}</DialogTitle>
                  {panel}
                </DialogContent>
              </Dialog>
            ) : null}
            <DockShelf aria-label={t('hiring-dashboard-ai-assistant:agent.dock.recentThreads')}>
              {dockTabs.map((tab) => (
                <DockPanelAnchor
                  className={activeDockTab?.threadId === tab.threadId ? 'sm:w-[480px]' : undefined}
                  key={tab.threadId}
                >
                  {!isNarrow && activeDockTab?.threadId === tab.threadId && panel}
                  <DockShelfItem className={activeDockTab?.threadId === tab.threadId ? 'invisible' : undefined}>
                    <DockShelfButton
                      aria-label={labelForTab(tab)}
                      className="text-muted-foreground"
                      data-testid={`agent-dock-recent-${tab.threadId}`}
                      onClick={() => openShelfThread(tab.threadId)}
                    >
                      {titleForDockTab(tab, titleByThreadId, untitled)}
                    </DockShelfButton>
                    <DockShelfCloseButton
                      data-testid={`agent-dock-remove-${tab.threadId}`}
                      label={t('hiring-dashboard-ai-assistant:agent.dock.removeTab', {
                        title: titleForDockTab(tab, titleByThreadId, untitled),
                      })}
                      onClick={() => closeDockThread(tab.threadId)}
                    />
                  </DockShelfItem>
                </DockPanelAnchor>
              ))}
              <ThreadSwitcherComponent
                activeThreadId={threadId}
                onNewThread={() => newThread(shelfSurface)}
                onSelectThread={openShelfThread}
                singleTrigger
                threads={threads}
                triggerClassName="pointer-events-auto h-10 rounded-t-md rounded-b-none border-b-0 bg-card/75 shadow-xs backdrop-blur-md hover:bg-card/90 dark:bg-card/70 dark:hover:bg-card/85"
                triggerLabel={t('hiring-dashboard-ai-assistant:agent.threads.history')}
                triggerTestId="agent-dock-open"
                triggerTitle={t('hiring-dashboard-ai-assistant:agent.dock.agent')}
              />
            </DockShelf>
          </Dock>
        </AiElementsScope>
      </div>
    </div>
  );
};

const AgentChatDock: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const isNarrow = useMediaQuery(DOCK_PANEL_FULL_WIDTH_MEDIA_QUERY);
  const enabled = useFlag('AI_GLOBAL_AGENT');
  const session = useAgentChatSession();
  const presence = useAgentDockPresence();
  // The full-page Agent routes own the conversation surface outright, so the dock must be absent
  // from the DOM there rather than merely translated off-screen.
  if (presence === AGENT_DOCK_PRESENCE.UNMOUNTED) {
    return null;
  }
  return (
    <AgentChatDockView
      dependencies={{
        enabled,
        history,
        isNarrow,
        session,
        translate: (key, values) => t(key, values),
        AgentChatComponent: AgentChat,
        ThreadSwitcherComponent: ThreadSwitcher,
        TitleComponent: EditableAgentThreadTitle,
      }}
      visible={presence === AGENT_DOCK_PRESENCE.VISIBLE}
    />
  );
};

export default AgentChatDock;
